import React, { useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import toast from "react-hot-toast";
import {
  useGetBoUsersQuery,
  useCreateBoUserMutation,
  useUpdateBoUserRoleMutation,
  useDeactivateBoUserMutation,
  BoUser,
  BoRole,
} from "../../store/services/boUsers";
import { useGetTenantsQuery } from "../../store/services/tenants";
import { useGetBoMeQuery } from "../../store/services/api";

const ROLE_OPTIONS: { label: string; value: BoRole }[] = [
  { label: "Super Admin", value: "SUPER_ADMIN" },
  { label: "Tenant Admin", value: "TENANT_ADMIN" },
];

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface CreateForm {
  email: string;
  firstName: string;
  lastName: string;
  role: BoRole;
  tenantId: string | null;
}

const EMPTY_FORM: CreateForm = {
  email: "",
  firstName: "",
  lastName: "",
  role: "TENANT_ADMIN",
  tenantId: null,
};

// MT-017 — Panel de administración de usuarios de Backoffice. V1: acceso exclusivo SUPER_ADMIN.
export const BoUsers = () => {
  const { data: me, isLoading: meLoading } = useGetBoMeQuery();
  const { data: boUsers = [], isLoading } = useGetBoUsersQuery();
  const { data: tenants = [] } = useGetTenantsQuery({});
  const [createBoUser, { isLoading: creating }] = useCreateBoUserMutation();
  const [updateRole] = useUpdateBoUserRoleMutation();
  const [deactivate] = useDeactivateBoUserMutation();

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<CreateForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof CreateForm, string>>>({});

  const [roleTarget, setRoleTarget] = useState<BoUser | null>(null);
  const [newRole, setNewRole] = useState<BoRole>("TENANT_ADMIN");
  // Tenant destino al degradar a TENANT_ADMIN (SUPER_ADMIN es global, no lo necesita).
  const [newRoleTenantId, setNewRoleTenantId] = useState<string>("");
  const [deactivateTarget, setDeactivateTarget] = useState<BoUser | null>(null);

  const tenantOptions = useMemo(
    () => tenants.map((t: any) => ({ label: t.provider || t.id, value: t.id })),
    [tenants]
  );

  const tenantName = (id: string) =>
    tenants.find((t: any) => t.id === id)?.provider || id;

  // ----- Gate de acceso (criterio: solo SUPER_ADMIN en V1) -----
  if (meLoading) return <div className="p-4">Cargando…</div>;
  if (me?.role !== "SUPER_ADMIN") {
    return (
      <div className="p-4">
        <Message severity="warn" text="Acceso restringido: solo SUPER_ADMIN puede administrar usuarios de BO." />
      </div>
    );
  }

  // ----- Validación del formulario de alta -----
  const validate = (): boolean => {
    const e: Partial<Record<keyof CreateForm, string>> = {};
    if (!emailRegex.test(form.email.trim())) e.email = "Email inválido";
    if (!form.firstName.trim()) e.firstName = "Requerido";
    if (!form.lastName.trim()) e.lastName = "Requerido";
    if (!form.role) e.role = "Requerido";
    if (form.role === "TENANT_ADMIN" && !form.tenantId) e.tenantId = "Tenant requerido para Tenant Admin";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleCreate = async () => {
    if (!validate()) return;
    try {
      await createBoUser({
        email: form.email.trim(),
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        role: form.role,
        tenant_id: form.role === "TENANT_ADMIN" ? form.tenantId! : undefined,
      }).unwrap();
      toast.success(`Se envió invitación a ${form.email.trim()}`);
      setShowCreate(false);
      setForm(EMPTY_FORM);
      setErrors({});
    } catch (err: any) {
      toast.error(err?.data?.message || "No se pudo crear el usuario");
    }
  };

  const handleUpdateRole = async () => {
    if (!roleTarget) return;
    try {
      await updateRole({
        userId: roleTarget.userId,
        role: newRole,
        // Solo aplica al degradar a TENANT_ADMIN (rol por-tenant).
        tenantId: newRole === "TENANT_ADMIN" ? newRoleTenantId || undefined : undefined,
      }).unwrap();
      toast.success("Rol actualizado");
      setRoleTarget(null);
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.data?.message || "No se pudo actualizar el rol");
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    try {
      await deactivate({ userId: deactivateTarget.userId }).unwrap();
      toast.success("Acceso revocado");
      setDeactivateTarget(null);
    } catch (err: any) {
      toast.error(err?.data?.message || "No se pudo desactivar");
    }
  };

  return (
    <div className="p-4">
      <div className="flex justify-content-between align-items-center mb-3">
        <h2 className="m-0">Usuarios de Backoffice</h2>
        <Button label="Agregar usuario" icon="pi pi-plus" onClick={() => setShowCreate(true)} />
      </div>

      <DataTable value={boUsers} loading={isLoading} emptyMessage="Sin usuarios de BO" dataKey="userId">
        <Column header="Nombre" body={(r: BoUser) => `${r.firstName} ${r.lastName}`.trim()} />
        <Column field="email" header="Email" />
        <Column
          header="Rol"
          body={(r: BoUser) => (
            <Tag value={r.role} severity={r.role === "SUPER_ADMIN" ? "danger" : "info"} />
          )}
        />
        <Column header="Tenant" body={(r: BoUser) => tenantName(r.tenantId)} />
        <Column
          header="Estado"
          body={(r: BoUser) => (
            <Tag value={r.active ? "Activo" : "Inactivo"} severity={r.active ? "success" : "danger"} />
          )}
        />
        <Column
          header="Mobile"
          body={(r: BoUser) =>
            r.hasMobileProfile ? (
              <Tag value="App mobile" severity="warning" icon="pi pi-mobile" />
            ) : null
          }
        />
        <Column
          header="Acciones"
          body={(r: BoUser) => (
            <div className="flex gap-2">
              <Button
                label="Rol"
                icon="pi pi-user-edit"
                size="small"
                text
                onClick={() => {
                  setRoleTarget(r);
                  setNewRole(r.role);
                  // Prefijar el tenant actual del usuario (si tiene) para la degradación.
                  setNewRoleTenantId(r.tenantId || "");
                }}
              />
              {r.active && (
                <Button
                  label="Revocar"
                  icon="pi pi-ban"
                  size="small"
                  text
                  severity="danger"
                  onClick={() => setDeactivateTarget(r)}
                />
              )}
            </div>
          )}
        />
      </DataTable>

      {/* ----- Alta de usuario ----- */}
      <Dialog
        header="Agregar usuario de BO"
        visible={showCreate}
        style={{ width: "32rem" }}
        onHide={() => setShowCreate(false)}
      >
        <div className="flex flex-column gap-3 pt-2">
          <div className="flex flex-column gap-1">
            <label htmlFor="bo-email">Email</label>
            <InputText
              id="bo-email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={errors.email ? "p-invalid" : ""}
            />
            {errors.email && <small className="p-error">{errors.email}</small>}
          </div>
          <div className="flex gap-3">
            <div className="flex flex-column gap-1 flex-1">
              <label htmlFor="bo-first">Nombre</label>
              <InputText
                id="bo-first"
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                className={errors.firstName ? "p-invalid" : ""}
              />
              {errors.firstName && <small className="p-error">{errors.firstName}</small>}
            </div>
            <div className="flex flex-column gap-1 flex-1">
              <label htmlFor="bo-last">Apellido</label>
              <InputText
                id="bo-last"
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                className={errors.lastName ? "p-invalid" : ""}
              />
              {errors.lastName && <small className="p-error">{errors.lastName}</small>}
            </div>
          </div>
          <div className="flex flex-column gap-1">
            <label htmlFor="bo-role">Rol</label>
            <Dropdown
              id="bo-role"
              value={form.role}
              options={ROLE_OPTIONS}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setForm({ ...form, role: e.value, tenantId: e.value === "SUPER_ADMIN" ? null : form.tenantId })}
            />
          </div>
          {form.role === "TENANT_ADMIN" && (
            <div className="flex flex-column gap-1">
              <label htmlFor="bo-tenant">Tenant / Organización</label>
              <Dropdown
                id="bo-tenant"
                value={form.tenantId}
                options={tenantOptions}
                optionLabel="label"
                optionValue="value"
                filter
                placeholder="Seleccionar tenant"
                onChange={(e) => setForm({ ...form, tenantId: e.value })}
                className={errors.tenantId ? "p-invalid" : ""}
              />
              {errors.tenantId && <small className="p-error">{errors.tenantId}</small>}
            </div>
          )}
          <div className="flex justify-content-end gap-2 pt-2">
            <Button label="Cancelar" text onClick={() => setShowCreate(false)} />
            <Button label="Enviar invitación" icon="pi pi-send" loading={creating} onClick={handleCreate} />
          </div>
        </div>
      </Dialog>

      {/* ----- Cambio de rol ----- */}
      <Dialog
        header="Modificar rol"
        visible={!!roleTarget}
        style={{ width: "26rem" }}
        onHide={() => setRoleTarget(null)}
      >
        {roleTarget && (
          <div className="flex flex-column gap-3 pt-2">
            <span>
              Usuario: <strong>{roleTarget.email}</strong>
            </span>
            <Dropdown
              value={newRole}
              options={ROLE_OPTIONS}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setNewRole(e.value)}
            />
            {/* Tenant destino: solo para TENANT_ADMIN (SUPER_ADMIN es un rol global). */}
            {newRole === "TENANT_ADMIN" && (
              <div className="flex flex-column gap-1">
                <label className="text-sm text-color-secondary">Tenant a administrar</label>
                <Dropdown
                  value={newRoleTenantId || null}
                  options={tenantOptions}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Seleccionar tenant"
                  filter
                  onChange={(e) => setNewRoleTenantId(e.value)}
                />
              </div>
            )}
            <div className="flex justify-content-end gap-2">
              <Button label="Cancelar" text onClick={() => setRoleTarget(null)} />
              <Button
                label="Guardar"
                onClick={handleUpdateRole}
                disabled={
                  (newRole === roleTarget.role &&
                    (newRole !== "TENANT_ADMIN" || newRoleTenantId === roleTarget.tenantId)) ||
                  (newRole === "TENANT_ADMIN" && !newRoleTenantId)
                }
              />
            </div>
          </div>
        )}
      </Dialog>

      {/* ----- Confirmación de baja ----- */}
      <Dialog
        header="Revocar acceso"
        visible={!!deactivateTarget}
        style={{ width: "26rem" }}
        onHide={() => setDeactivateTarget(null)}
      >
        {deactivateTarget && (
          <div className="flex flex-column gap-3 pt-2">
            <Message
              severity="warn"
              text={`Se revocará el acceso al BO de ${deactivateTarget.email}. Su perfil mobile no se ve afectado.`}
            />
            <div className="flex justify-content-end gap-2">
              <Button label="Cancelar" text onClick={() => setDeactivateTarget(null)} />
              <Button label="Revocar acceso" severity="danger" icon="pi pi-ban" onClick={handleDeactivate} />
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};
